import React, { useEffect, useState } from "react";
import _ from "lodash";
import ActivityForm from "./ActivityForm";
import { getProductNameBy } from "../../../services/Product/ProductService/productService";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import ReactGA from "react-ga4";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../../services/ReactQuery/reactQueryService";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import { Activity } from "../../../types/Activity";
import { UseFormProps } from "../../../hooks/useForm";
import { UseForm } from "../../../types/UseForm";
import { useCurrentEditor } from "@tiptap/react";
import LegalbirdTipTapEditorProvider from "../../LbEditor/LegalbirdTipTapEditorProvider";
import dayjs from "dayjs";

type EditActivityProps = {
  open: boolean;
  renderInDrawer?: boolean;
  closeDialog: Function;
  activity: Activity;
  updateActivities: Function;
  productClassName?: string;
  overwrittenHandleSave?: Function;
};

const EditActivity = (props: EditActivityProps) => {
  return (
    <LegalbirdTipTapEditorProvider placeholder={"Notiz"}>
      <EditActivityInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
};

export default EditActivity;

const EditActivityInner = ({
  open,
  renderInDrawer,
  closeDialog,
  activity,
  updateActivities,
  productClassName,
  overwrittenHandleSave,
}: EditActivityProps) => {
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const [initialValues, setInitialValues] = useState<Record<string, any>>();
  const [activityFormKey, setActivityFormKey] = useState(0);
  const { getUserData } = useBackofficeUser();
  const { editor } = useCurrentEditor();

  useEffect(() => {
    const formValues = ["type", "subject", "dueDate", "dueTime", "mandatory", "assignedUser", "note", "duration"];
    let activityValues: Record<string, any> = _.merge({}, _.pick(activity, formValues));
    if (typeof activity.assignedUser === "object" && activity.assignedUser) {
      activityValues.assignedUser = activity.assignedUser["@id"]!;
    }
    if (activityValues.dueDate && dayjs(activityValues.dueDate).isValid()) {
      activityValues.dueDate = dayjs(activityValues.dueDate);
    }
    if (activityValues.dueTime === "allDay") {
      activityValues.dueTime = "";
    }
    editor?.commands.setContent(activityValues.note);
    setInitialValues(activityValues);
  }, [activity]);

  useEffect(() => {
    if (initialValues) {
      setActivityFormKey((prevState) => prevState + 1);
    }
  }, [initialValues]);

  const buildActivityData = (values: UseForm["values"]) => {
    let updateData = _.merge({}, values);
    updateData.dueTime = updateData.dueTime !== "" ? updateData.dueTime : "allDay";
    updateData.note = getEditorHtml(editor, true);
    updateData.mandatory =
      typeof updateData.mandatory === "string" ? updateData.mandatory === "true" : updateData.mandatory;
    return updateData;
  };

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    if (overwrittenHandleSave) {
      return;
    }
    const updateData = buildActivityData(values);

    await updateMutation.mutateAsync({ id: activity.id || 0, uri: "activities", data: updateData });
    await updateActivities();
    trackPostponedActivity(updateData);

    setInitialValues(values);
    closeDialog();

  };

  const trackPostponedActivity = (updateData: Record<string, any>) => {
    const assignedUser =
      typeof activity.assignedUser === "object" ? activity.assignedUser : getUserData(activity.assignedUser);
    if (!assignedUser) {
      return;
    }
    if (dayjs(activity.dueDate) !== updateData.dueDate && dayjs(activity.dueDate) < updateData.dueDate) {
      ReactGA.set({ userId: assignedUser.id });
      ReactGA.event({
        category: "Activity",
        action: "Postponed",
        label: assignedUser.person.givenName + " " + assignedUser.person.familyName,
      });
    }
  };

  const calculatedProductClassName =
    productClassName || getProductNameBy("lbProductClass", activity.case.lbProductClass);

  if (!initialValues || !calculatedProductClassName) {
    return null;
  }

  return (
    <ActivityForm
      renderAsModal={!renderInDrawer}
      open={open}
      key={"activityForm" + activityFormKey}
      initialValues={initialValues}
      onSubmit={onSubmit}
      closeDialog={closeDialog}
      productClassName={calculatedProductClassName}
      activity={activity}
      autosave={
        overwrittenHandleSave
          ? async (values: Record<string, any>) => {
              await overwrittenHandleSave(buildActivityData(values));
            }
          : undefined
      }
    />
  );
};
